import api from '../../services/api';

// Limites oficiais da Meta para figurinhas (image/webp).
export const STICKER_STATIC_MAX_BYTES = 100 * 1024;
export const STICKER_ANIMATED_MAX_BYTES = 500 * 1024;
export const STICKER_SIZE_PX = 512;
export const STICKERS_CHANGED_EVENT = 'syncs:figurinhas-alteradas';

export function notifyStickersChanged() {
  window.dispatchEvent(new Event(STICKERS_CHANGED_EVENT));
}

function ascii(bytes, start, end) {
  return String.fromCharCode(...bytes.slice(start, end));
}

// null = nao e WebP; true/false = animada ou nao.
export function webpAnimation(arrayBuffer) {
  const bytes = new Uint8Array(arrayBuffer || new ArrayBuffer(0));
  if (bytes.length < 30 || ascii(bytes, 0, 4) !== 'RIFF' || ascii(bytes, 8, 12) !== 'WEBP') return null;
  const chunk = ascii(bytes, 12, 16);
  if (chunk === 'VP8X') return (bytes[20] & 0x02) !== 0;
  if (chunk === 'VP8 ' || chunk === 'VP8L') return false;
  return null;
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
    image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Não foi possível ler esta imagem.')); };
    image.src = url;
  });
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', quality));
}

// PNG, JPG ou WebP estatico viram WebP 512x512 com fundo transparente, ate 100 KB.
// WebP animado segue como esta (o navegador nao recodifica animacao).
export async function fileToStickerBlob(file) {
  if (!file) throw new Error('Escolha uma imagem.');
  if (!/^image\/(png|jpeg|webp)$/.test(file.type)) {
    throw new Error('Use uma imagem PNG, JPG ou WebP.');
  }
  if (file.type === 'image/webp' && webpAnimation(await file.arrayBuffer()) === true) {
    if (file.size > STICKER_ANIMATED_MAX_BYTES) {
      throw new Error('Figurinha animada acima de 500 KB, o limite da Meta.');
    }
    return file;
  }

  const image = await loadImage(file);
  const canvas = document.createElement('canvas');
  canvas.width = STICKER_SIZE_PX;
  canvas.height = STICKER_SIZE_PX;
  const context = canvas.getContext('2d');
  const scale = Math.min(STICKER_SIZE_PX / image.width, STICKER_SIZE_PX / image.height);
  const width = Math.round(image.width * scale);
  const height = Math.round(image.height * scale);
  context.drawImage(image, (STICKER_SIZE_PX - width) / 2, (STICKER_SIZE_PX - height) / 2, width, height);

  for (const quality of [0.92, 0.8, 0.65, 0.5, 0.35, 0.2]) {
    const blob = await canvasToBlob(canvas, quality);
    if (!blob || blob.type !== 'image/webp') {
      throw new Error('Este navegador não converte imagens para WebP. Use Chrome ou Edge, ou escolha um arquivo .webp.');
    }
    if (blob.size <= STICKER_STATIC_MAX_BYTES) return blob;
  }
  throw new Error('Não foi possível deixar a figurinha abaixo de 100 KB. Tente uma imagem mais simples.');
}

export async function uploadSticker(blob, { escopo = 'pessoal', nome = '' } = {}) {
  const form = new FormData();
  form.append('file', new File([blob], 'figurinha.webp', { type: 'image/webp' }));
  form.append('escopo', escopo);
  if (nome) form.append('nome', nome);
  const response = await api.post('/figurinhas', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  notifyStickersChanged();
  return response.data;
}
